import { createEffect, Match, on, Switch } from "solid-js";
import { DocumentPanelContent } from "~/modules/shared/chat/components/DocumentPanelContent/DocumentPanelContent";
import { ProfilePanel } from "~/modules/shared/chat/components/ProfilePanel";
import { SidePaneResizer } from "~/modules/shared/chat/components/SidePaneResizer/SidePaneResizer";
import {
	type SidePaneResize,
	useSidePaneResize,
} from "~/modules/shared/chat/components/SidePaneResizer/useSidePaneResize";
import { useDocumentPreviewPanel } from "~/modules/shared/chat/components/useDocumentPreviewPanel";
import { useProfileMentionPanel } from "~/modules/shared/chat/components/useProfileMentionPanel";
import { useVideoEvidencePanel } from "~/modules/shared/chat/components/useVideoEvidencePanel";
import { VideoPanelContent } from "~/modules/shared/chat/components/VideoPanelContent/VideoPanelContent";
import { AskPageAnswersPane } from "./AskPageAnswersPane";
import "./AskPageSidePane.css";

type Props = {
	/** Offer the playing clip to a board; the viewer needs to be signed in to have boards. */
	showAddToBoardButton: boolean;
	/** Signed-out readers see only the summary a profile mention carries. */
	isSignedIn: boolean;
	/** The thread's answers list, opened from the page header; omit where it isn't offered. */
	answers?: {
		open: boolean;
		questionIds: string[];
		onClose: () => void;
	};
};

/**
 * Shows whatever the chat transcript asks to open beside it: an answer's
 * clips, a page of a document, or a mentioned creator's profile. The
 * thread's answers list, when open, covers all of them. The
 * transcript asks with a `chatevent`; in the studio, ChatLayout's side pane
 * answers it, but AskPage threads have no ChatLayout.
 *
 * Rendered as the last child of `.askgvt1-ask-page-shell`, after the
 * `.askgvt1-creator-page` column it sits beside.
 */
export const AskPageSidePane = (props: Props) => {
	const profilePanel = useProfileMentionPanel();
	const documentPanel = useDocumentPreviewPanel({
		onOpen: () => {
			profilePanel.close();
			videoPanel.close();
			props.answers?.onClose();
		},
	});
	// Kept while the answers list covers it, so the clips come back when the
	// list is closed.
	const videoPanel = useVideoEvidencePanel({
		onOpen: () => {
			profilePanel.close();
			documentPanel.close();
			props.answers?.onClose();
		},
	});
	createEffect(
		on(
			() => props.answers?.open,
			(open) => {
				if (open) {
					documentPanel.close();
				}
			},
			{ defer: true },
		),
	);

	const resizeBeside = (pane: () => HTMLElement | undefined) =>
		useSidePaneResize(() => {
			const row = pane()?.parentElement;
			const column = pane()?.previousElementSibling;
			return row && column instanceof HTMLElement
				? { row, column, content: column }
				: undefined;
		});
	// Each pane keeps its own width: a document is read at a width the
	// answers list doesn't need.
	let documentPane: HTMLElement | undefined;
	let answersPane: HTMLElement | undefined;
	const documentResize = resizeBeside(() => documentPane);
	const answersResize = resizeBeside(() => answersPane);

	/** Only the document and answers panes are resizable, so only they override the width. */
	const resizedStyle = (resize: SidePaneResize) => {
		const width = resize.columnWidth();
		return width === undefined
			? undefined
			: {
					"--side-pane-column-width": `${width}px`,
					"--side-pane-overlap": `${resize.overlap()}px`,
				};
	};

	return (
		<>
			<Switch>
				<Match when={props.answers?.open ? props.answers : undefined}>
					{(answers) => (
						<aside
							ref={answersPane}
							class="ask-page-side-pane ask-page-side-pane--answers"
							classList={{
								"ask-page-side-pane--resized":
									resizedStyle(answersResize) !== undefined,
							}}
							style={resizedStyle(answersResize)}
							aria-label="Answers"
						>
							<SidePaneResizer
								resize={answersResize}
								label="Resize the answers panel"
							/>
							<AskPageAnswersPane
								questionIds={answers().questionIds}
								onClose={answers().onClose}
							/>
						</aside>
					)}
				</Match>
				<Match when={videoPanel.opened()}>
					{(opened) => (
						<aside class="ask-page-side-pane" aria-label="Clips">
							<VideoPanelContent
								class="ask-page-side-pane__content"
								videoIndex={opened().index}
								videoEvidence={opened().clips}
								onClose={videoPanel.close}
								showAddToBoardButton={props.showAddToBoardButton}
								autoPlay={false}
							/>
						</aside>
					)}
				</Match>
				<Match when={documentPanel.preview()}>
					{(preview) => (
						<aside
							ref={documentPane}
							class="ask-page-side-pane ask-page-side-pane--document"
							classList={{
								"ask-page-side-pane--resized":
									resizedStyle(documentResize) !== undefined,
							}}
							style={resizedStyle(documentResize)}
							aria-label="Document"
						>
							<SidePaneResizer
								resize={documentResize}
								label="Resize the document preview"
							/>
							<DocumentPanelContent
								content={preview()}
								onClose={documentPanel.close}
							/>
						</aside>
					)}
				</Match>
			</Switch>
			<ProfilePanel
				isOpen={profilePanel.isOpen}
				mention={profilePanel.profileMention}
				onClose={profilePanel.close}
				summaryOnly={!props.isSignedIn}
			/>
		</>
	);
};
